import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUser, FiPlus, FiMail, FiShield, FiToggleLeft, FiToggleRight, FiSend } from 'react-icons/fi';
import UserModal from '../components/UserModal';

const Users = () => {
  const { isDark } = useTheme();
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    setToggling(userId);
    try {
      const response = await api.patch(`/users/${userId}/status`, {
        is_active: !currentStatus
      });
      showSuccess(
        `User ${!currentStatus ? 'activated' : 'deactivated'} successfully!`,
        'Status Updated'
      );
      fetchUsers();
    } catch (error) {
      showError(
        error.response?.data?.error || 'Failed to update user status',
        'Error'
      );
    } finally {
      setToggling(null);
    }
  };

  const handleResendInvite = async (userId) => {
    setResendingInvite(userId);
    try {
      await api.post(`/users/${userId}/resend-invite`);
      showSuccess('Set password email sent. The user can use the new link to set their password.', 'Email sent');
      fetchUsers();
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to send email', 'Error');
    } finally {
      setResendingInvite(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-gray-400' : 'border-white'}`}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pr-0 lg:pr-20">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1 lg:mb-2">Users</h1>
          <p className="text-white/80 text-sm lg:text-base">Manage team members</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowModal(true)}
          className="bg-white text-primary-600 px-5 py-3.5 rounded-xl font-semibold flex items-center justify-center space-x-2 active:bg-white/90 transition-all touch-manipulation shadow-lg w-full sm:w-auto"
        >
          <FiPlus size={20} />
          <span>Add User</span>
        </motion.button>
      </div>

      {users.length === 0 ? (
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-12 border border-white/20 text-center">
          <FiUser className="mx-auto text-white/50 mb-4" size={48} />
          <p className="text-white/80 text-lg mb-2">No users yet</p>
          <p className="text-white/60 text-sm">Add your first team member</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
          <AnimatePresence>
            {users.map((user, index) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white/10 backdrop-blur-md rounded-xl p-6 border transition-all ${
                  user.is_active === false ? 'opacity-60 border-red-500/30' : 'border-white/20 hover:bg-white/15'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      user.is_active === false ? 'bg-gray-500' : 'bg-primary-500'
                    }`}>
                      <FiUser className="text-white" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{user.name}</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <FiMail className="text-white/60" size={14} />
                        <p className="text-white/60 text-sm">{user.email}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span
                      className={`${
                        user.role === 'admin'
                          ? 'bg-purple-500'
                          : user.role === 'tech'
                            ? 'bg-teal-600'
                            : 'bg-blue-500'
                      } text-white text-xs px-3 py-1 rounded-full capitalize flex items-center space-x-1`}
                    >
                      {user.role === 'admin' && <FiShield size={12} />}
                      <span>{user.role === 'tech' ? 'Technical' : user.role}</span>
                    </span>
                    <span
                      className={`text-xs px-3 py-1 rounded-full ${
                        user.is_active === false
                          ? 'bg-red-500/20 text-red-300 border border-red-500/50'
                          : 'bg-green-500/20 text-green-300 border border-green-500/50'
                      }`}
                    >
                      {user.is_active === false ? 'Inactive' : 'Active'}
                    </span>
                  </div>
                  {user.invite_token_expires && (
                    <button
                      type="button"
                      onClick={() => handleResendInvite(user.id)}
                      disabled={resendingInvite === user.id}
                      className="w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 transition-all text-amber-200 text-sm font-medium disabled:opacity-50"
                    >
                      {resendingInvite === user.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-300" />
                      ) : (
                        <>
                          <FiSend size={16} />
                          <span>Resend set password email</span>
                        </>
                      )}
                    </button>
                  )}
                  {user.role !== 'admin' && (
                    <button
                      onClick={() => handleToggleStatus(user.id, user.is_active)}
                      disabled={toggling === user.id}
                      className="w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all text-white text-sm font-medium disabled:opacity-50"
                    >
                      {toggling === user.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : user.is_active ? (
                        <>
                          <FiToggleRight size={18} />
                          <span>Deactivate</span>
                        </>
                      ) : (
                        <>
                          <FiToggleLeft size={18} />
                          <span>Activate</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <AnimatePresence>
        {showModal && (
          <UserModal
            onClose={() => {
              setShowModal(false);
              fetchUsers();
            }}
            onSuccess={fetchUsers}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Users;
